import { Component, DestroyRef, OnInit, inject } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatMenuModule } from '@angular/material/menu'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { EBvFieldType, EBvTaskStatus } from '@modules'
import { firstValueFrom } from 'rxjs'
import { distinctUntilChanged, filter as rxFilter, map as rxMap, skip, tap } from 'rxjs/operators'
import { PropertyType, TaskView } from '@ziptility/graphql'
import { FilterName, TaskViewStore } from './task-view.store'
import { ViewFilterOptionsComponent } from '../views/view-filter-options.component'
import { CommonModule } from '@angular/common'
import { IconsModule } from '@ziptility/icons'
import { BvViewFilter } from '../views/models'
import { MatSelectModule } from '@angular/material/select'
import { DialogService } from '@ziptility/components/dialogs'
import { SaveViewModal } from '../views/save-view-modal.component'
import { ViewFilterSelections, InputModal, PropertyValueFilterForm } from '../views'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

@Component({
  selector: 'task-list',
  template: `
    <mat-menu #filterOptionsMenu (closed)="taskViewStore.selectFilter(undefined)">
      <view-filter-options
        [filters]="taskViewStore.filters"
        [selectedFilter]="selectedFilter$ | async"
        [filterOptions]="filterOptions()"
        (onFilterChange)="taskViewStore.onFilterToggle($event.filter, $event.option)"
        (onSelectFilter)="selectFilter($event.event, $event.filterName)"
      ></view-filter-options>
    </mat-menu>

    <div class="h-full flex flex-col overflow-hidden">
      <router-outlet name="task"></router-outlet>
      <div>
        <div class="flex items-center justify-between px-8 py-3 border-b border-gray-200 ">
          <div class="flex items-center space-x-5 flex-grow">
            <view-filter-selections
              [menu]="filterOptionsMenu"
              [filters]="selectedFilters()"
              (onRemoveFilter)="taskViewStore.removeFilter($event.filter.title)"
              (onSelectDisplayValue)="updateFilter($event)"
            ></view-filter-selections>

            <button class="flex items-center" #filterMenuTrigger [mat-menu-trigger-for]="filterOptionsMenu">
              <i-feather name="plus" class="feather-icon-sm"> </i-feather>
            </button>

            <div class="flex-grow"></div>
            <div class="flex items-center space-x-2">
              @if ((selectedView$ | async)?.id; as viewId) {
                <button class="bv-button-link link text-sm mr-3 text-red-brand" (click)="saveView()">Save</button>
              }
              @if (!(selectedView$ | async)?.id; as viewId) {
                <button class="bv-button-link link text-sm mr-3 text-red-brand" (click)="saveView()">Save</button>
              }

              <div class="form-select cursor-pointer" [mat-menu-trigger-for]="viewsMenu">
                {{ (selectedView$ | async)?.name ?? 'Views' }}
              </div>

              <mat-menu #viewsMenu>
                <div class="bv-menu">
                  @if (!(views$ | async)?.length) {
                    <div class="px-3 text-center text-gray-500">No Saved Views</div>
                  }
                  @if ((views$ | async)?.length) {
                    <div class="bv-menu-item" (click)="onChangeView(null)">None</div>
                  }
                  @for (view of views$ | async; track view.id) {
                    <div class="bv-menu-item">
                      <div>
                        <div class="flex items-center justify-between space-x-5">
                          <div (click)="onChangeView(view.id)">{{ view.name }}</div>
                          <button
                            class="flex items-center justify-center"
                            (click)="$event.stopPropagation()"
                            [matMenuTriggerFor]="viewOptionsMenu"
                          >
                            <i-feather name="more-horizontal" class="feather-icon-sm"></i-feather>
                          </button>
                        </div>
                        <mat-menu #viewOptionsMenu>
                          <button mat-menu-item (click)="$event.stopPropagation(); editView(view)">Rename</button>
                          <button mat-menu-item (click)="$event.stopPropagation(); deleteView(view)">Delete</button>
                        </mat-menu>
                      </div>
                    </div>
                  }
                </div>
              </mat-menu>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-grow overflow-hidden">
        <router-outlet></router-outlet>
      </div>
    </div>
  `,
  host: {
    class: 'block h-full overflow-hidden',
  },
  imports: [
    CommonModule,
    IconsModule,
    MatMenuModule,
    ViewFilterOptionsComponent,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    MatSelectModule,
    ViewFilterSelections,
  ],
})
export class TaskReportsContainer implements OnInit {
  EBvFieldType = EBvFieldType
  EBvTaskStatus = EBvTaskStatus

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly matDialog = inject(MatDialog)
  private readonly dialogService = inject(DialogService)
  protected readonly taskViewStore = inject(TaskViewStore)
  readonly destroyRef = inject(DestroyRef)

  protected readonly views$ = this.taskViewStore.views$
  protected readonly selectedView$ = this.taskViewStore.selectedView$
  protected readonly selectedFilter$ = this.taskViewStore.selectedFilter$

  protected readonly filterOptions = toSignal(this.taskViewStore.filterOptions$, { initialValue: {} })
  protected readonly selectedFilters = toSignal(
    this.taskViewStore.select((s) => s.selectedFilters.filter((f) => !['Team', 'Status'].includes(f.title))),
    { initialValue: [] },
  )
  visibleTemplateFieldIds: string[] = []
  columnSearch: string | undefined

  async ngOnInit() {
    await this.taskViewStore.load()

    const viewId = this.route.queryParams.pipe(
      rxMap((p) => p.viewId),
      distinctUntilChanged(),
    )
    const mapId = this.route.params.pipe(
      rxMap((p) => p.mapId),
      distinctUntilChanged(),
    )

    viewId
      .pipe(
        tap((viewId) => this.taskViewStore.selectView({ id: viewId, mode: 'reports' })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()

    mapId
      .pipe(
        skip(1), // Skip the initial emission to avoid clearing the view on component load
        tap(() => {
          this.onChangeView(null)
          this.taskViewStore.selectView({ id: undefined, mode: 'reports' })
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  onChangeView(viewId: string | null) {
    this.router.navigate([], {
      queryParams: {
        viewId,
      },
      queryParamsHandling: 'merge',
    })
  }

  async selectFilter($event: MouseEvent, filterName: FilterName | undefined) {
    switch (filterName) {
      case 'AssetProperty':
      case 'TaskTemplate Field': {
        const source = filterName === 'AssetProperty' ? 'Asset' : 'Task'
        const ref = this.matDialog.open(PropertyValueFilterForm, {
          data: {
            filterOptions:
              source == 'Asset'
                ? await this.taskViewStore.getAssetFilterOptions()
                : await firstValueFrom(this.taskViewStore.taskProperties$),
          },
        })
        ref
          .afterClosed()
          .pipe(rxFilter((viewFilter) => !!viewFilter))
          .subscribe((viewFilter: BvViewFilter) => {
            viewFilter.source = source
            this.taskViewStore.addFilter(viewFilter)
          })
        break
      }

      case 'Time Tracking': {
        this.taskViewStore.addFilter(new BvViewFilter('Has Time Tracking', PropertyType.BooleanType, ['true']))
        break
      }
      case 'Inventory': {
        this.taskViewStore.addFilter(new BvViewFilter('Has Inventory', PropertyType.BooleanType, ['true']))
        break
      }
      default:
        $event.stopPropagation()
        this.taskViewStore.selectFilter(filterName)
    }
  }

  async updateFilter({ filter: viewFilter }: { index: number; filter: BvViewFilter }) {
    if (this.taskViewStore.isTopLevelFilter(viewFilter.title)) {
      this.taskViewStore.selectFilter(viewFilter.title)
      return
    }

    const source = viewFilter.source
    const ref = this.matDialog.open(PropertyValueFilterForm, {
      data: {
        viewFilter: viewFilter,
        filterOptions:
          source === 'Asset'
            ? await this.taskViewStore.getAssetFilterOptions()
            : await firstValueFrom(this.taskViewStore.taskProperties$),
      },
    })

    ref
      .afterClosed()
      .pipe(rxFilter((filter) => !!filter))
      .subscribe((filter: BvViewFilter) => {
        filter.source = source
        this.taskViewStore.updateFilter({ filter, value: filter.value })
      })
  }

  async saveView() {
    const res = await firstValueFrom(this.matDialog.open(SaveViewModal).afterClosed())
    if (!res) {
      return
    }

    switch (res) {
      case 'clone':
        this.createView()
        break
      case 'update': {
        const view = await firstValueFrom(this.selectedView$)
        if (view) {
          this.taskViewStore.updateView({
            id: view.id,
            name: view.name,
          })
          return
        }

        break
      }
      default:
    }
  }

  private async getViewTitle(
    modalTitle = 'New View',
    buttonTitle = 'Create',
    value: string | undefined = undefined,
  ): Promise<string | undefined> {
    const ref = this.matDialog.open(InputModal)
    ref.componentInstance.title = modalTitle
    ref.componentInstance.buttonTitle = buttonTitle
    ref.componentInstance.value = value ?? ''
    return firstValueFrom(ref.afterClosed())
  }

  async createView() {
    const name = await this.getViewTitle()
    if (!name) {
      return
    }
    this.taskViewStore.createView(name)
  }

  async editView(view: TaskView) {
    const name = await this.getViewTitle('Rename', 'Update', view.name)
    if (!name) {
      return
    }

    this.taskViewStore.updateView({
      id: view.id,
      name,
    })
  }

  async deleteView(view: TaskView) {
    const res = await firstValueFrom(
      this.dialogService.confirmDialogObservable(
        `Delete View`,
        `Are you sure you want to delete this view? This action cannot be undone.`,
        `Delete`,
      ),
    )

    if (!res) {
      return
    }

    this.taskViewStore.deleteView(view.id)
  }
}
